/**
 * CR318: Nav Health Badge
 * 
 * Small indicator badge for nav items showing health status.
 * Clickable to open the health drawer.
 */

import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertTriangle, XCircle } from "lucide-react";
import { useProductionHealthStatus, type HealthLabel, type HealthStatus } from "@/hooks/useProductionHealth";

interface NavHealthBadgeProps {
  onClick?: () => void;
  showLabel?: boolean;
  className?: string;
}

function getBadgeConfig(status: HealthStatus, label: HealthLabel): {
  icon: typeof AlertTriangle | typeof XCircle;
  colorClass: string;
  tooltipText: string;
} | null {
  if (status === "healthy") return null;
  
  const tooltipMap: Record<string, string> = {
    "No Snapshot": "No live snapshot published (public shows 0 deals). Click for details.",
    "Edge Down": "Edge function unreachable. Click for details.",
    "Auth": "Authentication issue detected. Click for details.",
    "DB": "Database connectivity issue. Click for details.",
    "Unknown": "Unknown production issue. Click for details.",
  };
  
  return {
    icon: status === "outage" ? XCircle : AlertTriangle,
    colorClass: status === "outage" 
      ? "bg-red-500/20 text-red-600 border-red-500/30 hover:bg-red-500/30" 
      : "bg-amber-500/20 text-amber-600 border-amber-500/30 hover:bg-amber-500/30",
    tooltipText: label ? tooltipMap[label] || "Production issue detected. Click for details." : "Production issue detected. Click for details.",
  };
}

export function NavHealthBadge({ onClick, showLabel = true, className = "" }: NavHealthBadgeProps) {
  const { status, label, isHealthy } = useProductionHealthStatus();
  
  if (isHealthy) return null;
  
  const config = getBadgeConfig(status, label);
  if (!config) return null;
  
  const Icon = config.icon;
  
  // Truncate label to fit nav
  const displayLabel = label && label.length > 12 ? label.slice(0, 10) + "…" : label;
  
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge 
          variant="outline"
          className={`cursor-pointer text-[10px] px-1.5 py-0.5 ${config.colorClass} ${className}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick?.();
          }}
        >
          <Icon className="h-3 w-3 mr-0.5" />
          {showLabel && displayLabel}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-[200px]">
        <p className="text-xs">{config.tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  );
}

// Compact dot-only version for tight spaces
export function NavHealthDot({ onClick, className = "" }: { onClick?: () => void; className?: string }) {
  const { status, isHealthy } = useProductionHealthStatus();
  
  if (isHealthy) return null;
  
  const colorClass = status === "outage" ? "bg-red-500" : "bg-amber-500";
  
  return (
    <span 
      className={`inline-block w-2 h-2 rounded-full ${colorClass} ${className} cursor-pointer`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.();
      }}
    />
  );
}
